import React, { useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ChatSupportBot from './components/ChatSupportBot';
import ErrorBoundary from './components/ErrorBoundary';
import SentryTestButton from './components/SentryTestButton';
import { Sentry } from './config/sentry.config';

// Browser maintenance for preventing crashes
import browserMaintenance from './utils/browserMaintenance';

// Progressive loading and lazy loading system
import { LazyPages, LoadingFallback, ProgressiveLoader, initializeProgressiveLoading } from './utils/lazyLoading';

// Sentry Debug Page (development only)
import SentryDebugPage from './pages/SentryDebugPage';

// Browser compatibility system
import browserCompatibility from './utils/browserCompatibility';

// Loading speed optimization system (auto-initialized on import)
/* eslint-disable no-unused-vars */
import loadingSpeedOptimizer from './utils/loadingSpeedOptimizer';

// Automatic error recovery system (auto-initialized on import)
import automaticErrorRecovery from './utils/automaticErrorRecovery';
/* eslint-enable no-unused-vars */

// Layouts (keep these non-lazy for immediate availability)
import AuthLayout from './layouts/AuthLayout';
import AdminLayout from './layouts/AdminLayout';

// Create Sentry-wrapped Router for enhanced route tracking
const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const App = () => {
  // Initialize all optimization and maintenance systems
  useEffect(() => {
    console.log('[App] Initializing optimization systems...');

    // Initialize browser maintenance (handles storage corruption, cache management, memory optimization)
    console.log('[App] Initializing browser maintenance...');
    browserMaintenance.init();

    // Initialize progressive loading system
    console.log('[App] Initializing progressive loading...');
    initializeProgressiveLoading();

    // Initialize loading speed optimizer (handles ~60% loading improvement)
    console.log('[App] Initializing loading speed optimizer...');
    // loadingSpeedOptimizer is auto-initialized when imported

    // Initialize automatic error recovery system
    console.log('[App] Initializing automatic error recovery...');
    // automaticErrorRecovery is auto-initialized when imported

    // Check browser compatibility and show warnings if needed
    const compatibilityStatus = browserCompatibility.initialize();
    if (!compatibilityStatus.supported) {
      const warningMessage = browserCompatibility.getBrowserSupportMessage();
      if (warningMessage) {
        console.warn('[App] Browser compatibility warning:', warningMessage);
        // Could show a toast notification here
        // toast.warn(warningMessage);
      }
    }

    // Log performance metrics
    const metrics = browserCompatibility.getPerformanceMetrics();
    console.log('[App] Performance metrics:', metrics);

    // Log system initialization completion
    console.log('[App] All optimization systems initialized successfully');
  }, []); // Empty dependency array since these are one-time initializations

  return (
    <ErrorBoundary>
      <AuthProvider>
        <NotificationProvider>
          <Router>
            <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />

            {/* Chat Support Bot - Only shown on authenticated routes */}
            <Routes>
              <Route path="/login" element={null} />
              <Route path="*" element={<ChatSupportBot />} />
            </Routes>

            {/* Sentry Test Button - Only shown in development */}
            <SentryTestButton />

            <Suspense fallback={<LoadingFallback message="Loading page..." size="lg" />}>
              <SentryRoutes>
                {/* Public Routes */}
                <Route path="/login" element={
                  <ProgressiveLoader>
                    <LazyPages.Login />
                  </ProgressiveLoader>
                } />
                <Route path="/terms" element={
                  <ProgressiveLoader>
                    <LazyPages.Terms />
                  </ProgressiveLoader>
                } />
                <Route path="/privacy" element={
                  <ProgressiveLoader>
                    <LazyPages.Privacy />
                  </ProgressiveLoader>
                } />

                {/* Redirect root to login */}
                <Route path="/" element={<Navigate to="/login" replace />} />

                {/* Sentry Debug Page (development only) */}
                {process.env.NODE_ENV === 'development' && (
                  <Route path="/sentry-debug" element={<SentryDebugPage />} />
                )}

                {/* Protected User Routes */}
                <Route element={<AuthLayout />}>
                  <Route path="/dashboard" element={
                    <ProgressiveLoader>
                      <LazyPages.Dashboard />
                    </ProgressiveLoader>
                  } />
                  <Route path="/scans" element={
                    <ProgressiveLoader>
                      <LazyPages.Scans />
                    </ProgressiveLoader>
                  } />
                  <Route path="/scans/new" element={
                    <ProgressiveLoader>
                      <LazyPages.NewScan />
                    </ProgressiveLoader>
                  } />
                  <Route path="/scans/:scanId" element={
                    <ProgressiveLoader>
                      <LazyPages.ScanDetailsImproved />
                    </ProgressiveLoader>
                  } />
                  <Route path="/data" element={
                    <ProgressiveLoader>
                      <LazyPages.DataExplorer />
                    </ProgressiveLoader>
                  } />
                  <Route path="/reports" element={
                    <ProgressiveLoader>
                      <LazyPages.Reports />
                    </ProgressiveLoader>
                  } />
                  <Route path="/security" element={
                    <ProgressiveLoader>
                      <LazyPages.EnhancedSecurityAnalysis />
                    </ProgressiveLoader>
                  } />
                  <Route path="/external-attack-surface" element={
                    <ProgressiveLoader>
                      <LazyPages.ExternalAttackSurface />
                    </ProgressiveLoader>
                  } />
                  <Route path="/attack-surface-issues" element={
                    <ProgressiveLoader>
                      <LazyPages.AttackSurfaceIssues />
                    </ProgressiveLoader>
                  } />
                  <Route path="/intelligence-coverage" element={
                    <ProgressiveLoader>
                      <LazyPages.IntelligenceCoverage />
                    </ProgressiveLoader>
                  } />
                  <Route path="/main-domains" element={
                    <ProgressiveLoader>
                      <LazyPages.MainDomains />
                    </ProgressiveLoader>
                  } />
                  <Route path="/subdomains" element={
                    <ProgressiveLoader>
                      <LazyPages.Subdomains />
                    </ProgressiveLoader>
                  } />
                  <Route path="/cms-security" element={
                    <ProgressiveLoader>
                      <LazyPages.CmsSecurity />
                    </ProgressiveLoader>
                  } />
                  <Route path="/intelligence-hub" element={
                    <ProgressiveLoader>
                      <LazyPages.IntelligenceHub />
                    </ProgressiveLoader>
                  } />
                  <Route path="/google-exposure" element={
                    <ProgressiveLoader>
                      <LazyPages.GoogleExposure />
                    </ProgressiveLoader>
                  } />
                  <Route path="/http-security" element={
                    <ProgressiveLoader>
                      <LazyPages.HttpHeaderSecurity />
                    </ProgressiveLoader>
                  } />
                  <Route path="/ssl-security" element={
                    <ProgressiveLoader>
                      <LazyPages.SslSecurity />
                    </ProgressiveLoader>
                  } />
                  <Route path="/hacked-email-monitoring" element={
                    <ProgressiveLoader>
                      <LazyPages.HackedEmailMonitoring />
                    </ProgressiveLoader>
                  } />
                  <Route path="/threat-advisory" element={
                    <ProgressiveLoader>
                      <LazyPages.ThreatAdvisory />
                    </ProgressiveLoader>
                  } />
                  <Route path="/breach-monitor" element={
                    <ProgressiveLoader>
                      <LazyPages.BreachMonitor />
                    </ProgressiveLoader>
                  } />
                  <Route path="/brand-monitoring" element={
                    <ProgressiveLoader>
                      <LazyPages.BrandMonitoring />
                    </ProgressiveLoader>
                  } />
                  <Route path="/social-pulse" element={
                    <ProgressiveLoader>
                      <LazyPages.SocialPulse />
                    </ProgressiveLoader>
                  } />
                  <Route path="/notifications" element={
                    <ProgressiveLoader>
                      <LazyPages.UserNotifications />
                    </ProgressiveLoader>
                  } />
                  <Route path="/profile" element={
                    <ProgressiveLoader>
                      <LazyPages.UserProfile />
                    </ProgressiveLoader>
                  } />
                  <Route path="/tickets" element={
                    <ProgressiveLoader>
                      <LazyPages.UserTickets />
                    </ProgressiveLoader>
                  } />
                  {/* Redirect old enhanced security analysis URL to new security route */}
                  <Route path="/enhanced-security-analysis" element={<Navigate to="/security" replace />} />
                </Route>

                {/* Protected Admin Routes */}
                <Route element={<AdminLayout />}>
                  <Route path="/admin" element={
                    <ProgressiveLoader>
                      <LazyPages.AdminDashboard />
                    </ProgressiveLoader>
                  } />
                  <Route path="/admin/smtp" element={
                    <ProgressiveLoader>
                      <LazyPages.AdminSmtp />
                    </ProgressiveLoader>
                  } />
                  <Route path="/admin/users" element={
                    <ProgressiveLoader>
                      <LazyPages.AdminUsers />
                    </ProgressiveLoader>
                  } />
                  <Route path="/admin/users/new" element={
                    <ProgressiveLoader>
                      <LazyPages.CreateUser />
                    </ProgressiveLoader>
                  } />
                  <Route path="/admin/users/:userId/edit" element={
                    <ProgressiveLoader>
                      <LazyPages.EditUser />
                    </ProgressiveLoader>
                  } />
                  <Route path="/admin/scans" element={
                    <ProgressiveLoader>
                      <LazyPages.AdminScans />
                    </ProgressiveLoader>
                  } />
                  <Route path="/admin/tickets" element={
                    <ProgressiveLoader>
                      <LazyPages.AdminTickets />
                    </ProgressiveLoader>
                  } />
                  <Route path="/admin/threat-advisory" element={
                    <ProgressiveLoader>
                      <LazyPages.AdminThreatAdvisory />
                    </ProgressiveLoader>
                  } />
                  <Route path="/admin/integrations" element={
                    <ProgressiveLoader>
                      <LazyPages.AdminIntegrations />
                    </ProgressiveLoader>
                  } />
                  <Route path="/admin/notifications" element={
                    <ProgressiveLoader>
                      <LazyPages.AdminNotifications />
                    </ProgressiveLoader>
                  } />
                  <Route path="/admin/cvemap" element={
                    <ProgressiveLoader>
                      <LazyPages.CVEMapSettings />
                    </ProgressiveLoader>
                  } />
                </Route>

                {/* 404 Not Found */}
                <Route path="*" element={
                  <ProgressiveLoader>
                    <LazyPages.NotFound />
                  </ProgressiveLoader>
                } />
              </SentryRoutes>
            </Suspense>
          </Router>
        </NotificationProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;
